import {useId, useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {Save} from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Label,
  Spinner,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {useRecordCalibration, useSubmissionHistory} from '@/api/hooks';
import {type SubmissionHistoryEvent} from '@/api/submissions';
import {getApiErrorMessage} from '@/api/client';
import {useAuth} from '@/context/useAuth';
import {toneBadge} from '@/lib/status';

// Calibration is recorded as a SubmissionCalibrated event rather than a row beside the submission,
// because the ask is for it to read as something that happened to the submission and sit with the
// rest of its lifecycle. That means this section renders straight off the history timeline - there
// is no separate calibration endpoint to read, only the one that appends.
export function CalibrationSection({
  submissionId,
  timeline,
  onRecorded
}: {
  readonly submissionId: string;
  readonly timeline: readonly SubmissionHistoryEvent[];
  // Lets the table row's "Calibrated" badge appear immediately - the history timeline this section
  // reads from is a separate query the table's own row data never touches.
  readonly onRecorded?: (completed: boolean) => void;
}) {
  const {user} = useAuth();
  const canCalibrate = user?.role === 'analyst_admin' || user?.role === 'admin';
  const record = useRecordCalibration(submissionId);

  const [comment, setComment] = useState('');
  const [completed, setCompleted] = useState(false);
  const [calibrationDate, setCalibrationDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const dateId = useId();
  const notesId = useId();
  const completedId = useId();

  const calibrations = timeline.filter(
    (event) => event.type === 'SubmissionCalibrated' || event.type === 'SubmissionCalibrationNoted'
  );

  async function handleRecord() {
    const trimmed = comment.trim();
    if (trimmed === '') {
      return;
    }

    setError(null);
    try {
      const wasCompleted = completed;
      await record.mutateAsync({
        comment: trimmed,
        completed,
        calibration_date: calibrationDate === '' ? null : calibrationDate
      });
      setComment('');
      setCompleted(false);
      setCalibrationDate('');
      onRecorded?.(wasCompleted);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Could not record the calibration.'));
    }
  }

  return (
    <div className="space-y-2">
      {calibrations.length === 0 ? (
        <Empty variant="outline" className="gap-2 p-4">
          <EmptyHeader className="gap-0.5">
            <EmptyTitle>No calibration notes</EmptyTitle>
            <EmptyDescription>
              Nothing has been recorded against this submission yet.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        // Plain ruled blocks rather than a Card per entry: the sheet is a popover surface, and a
        // card on it is all but invisible (1.07:1 in dark), so the rule is what separates entries.
        <ul>
          {calibrations.map((event) => (
            <li
              key={`${event.occurred_at}-${event.actor_email ?? ''}-${event.detail ?? ''}`}
              className="border-border space-y-1 border-t py-2">
              <div className="flex flex-wrap items-center gap-2">
                {event.type === 'SubmissionCalibrated' ? (
                  <Badge {...toneBadge('success')}>Calibrated</Badge>
                ) : (
                  <Badge variant="outline">Note</Badge>
                )}
                <span className="text-xs font-medium">
                  {event.actor_email?.split('@')[0] ?? event.actor_email ?? 'Unknown'}
                </span>
                {event.calibration_date ? (
                  <span className="text-xs">
                    Calibration date:{' '}
                    {new Date(`${event.calibration_date}T00:00:00`).toLocaleDateString()}
                  </span>
                ) : null}
                <span className="text-muted-foreground text-xs">
                  {new Date(event.occurred_at).toLocaleDateString()} ·{' '}
                  {formatDistanceToNow(new Date(event.occurred_at), {addSuffix: true})}
                </span>
              </div>
              <p className="text-xs whitespace-pre-wrap">{event.detail}</p>
            </li>
          ))}
        </ul>
      )}

      {canCalibrate ? (
        <div className="space-y-2">
          <Field orientation="horizontal" className="w-fit">
            <FieldLabel htmlFor={dateId}>Calibration date</FieldLabel>
            <Input
              id={dateId}
              type="date"
              value={calibrationDate}
              className="h-7 w-auto"
              onChange={(event) => {
                setCalibrationDate(event.target.value);
              }}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={notesId} className="sr-only">
              Calibration notes
            </FieldLabel>
            <Textarea
              id={notesId}
              value={comment}
              rows={2}
              className="min-h-0"
              placeholder="Calibration notes, e.g. what was concluded or when it will happen"
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
          </Field>
          <div className="flex items-center justify-between gap-2">
            {/* A plain Label rather than a Field: the Field's labelable scope would also point
                the checkbox at the label by aria-labelledby, naming it twice. */}
            <Label htmlFor={completedId} className="cursor-pointer">
              <Checkbox
                id={completedId}
                checked={completed}
                onCheckedChange={(next) => {
                  setCompleted(next);
                }}
              />
              Calibration complete
            </Label>
            <Button
              size="sm"
              disabled={record.isPending || comment.trim() === ''}
              onClick={() => {
                void handleRecord();
              }}>
              {record.isPending ? <Spinner /> : <Save />}
              {record.isPending ? 'Recording…' : 'Save note'}
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
    </div>
  );
}

/**
 Calibration notes on a submission under QA or QC review, recorded straight from the review. Saving one
 is its own call and never touches the review's outcome: a point worth raising at calibration doesn't
 mean the analyst's call was wrong.
 */
export function ReviewCalibrationNotes({submissionId}: {readonly submissionId: string}) {
  const {data: history} = useSubmissionHistory(submissionId, true);

  return (
    <FieldSet className="gap-1.5">
      <FieldLegend variant="label">Calibration notes</FieldLegend>
      <FieldDescription>
        For points worth discussing at calibration. Separate from the review outcome.
      </FieldDescription>
      <CalibrationSection submissionId={submissionId} timeline={history?.timeline ?? []} />
    </FieldSet>
  );
}
